import React from 'react'
import dayjs from 'dayjs'
import { GridColDef, GridRenderCellParams, GridValueGetterParams } from '@mui/x-data-grid-pro'
import { OnboardingsTableFields, OnboardingStatus } from './enums'
import { onboardingTableHeaderTitles } from './constants'
import { gridFormatters, gridValueGetters } from 'src/utils/grid'
import { Chip } from 'src/components/shared/Chip'
import { Onboarding } from 'src/interfaces/onboarding'
import { TableTags } from 'src/components/tags/TableTags'
import { OnboardingsItemActionButton } from 'src/components/onboarding/grid/action/OnboardingsItemActionButton'
import { colors } from 'src/theme'
import tw from 'twin.macro'
import { getOnboardingTableStepTitle } from 'src/utils/collectedData'
import { StepsChip } from 'src/components/shared/table/StepsChip'
import { OnboardingStatusChip } from 'src/components/onboarding/shared/OnboardingStatusChip'
import _ from 'lodash'
import { addS } from 'src/utils/baseUtils'
import { Tooltip } from '@mui/material'
import { contractorTypeTitles } from 'src/constants/contractor/constants'
import { ContractorTypeChip } from 'src/components/contractor-list/shared/ContractorTypeChip'
import { LimitedStringChip } from 'src/components/shared/LimitedStringChip'
import { ContractorLastActivity } from 'src/components/contractor-list/shared/ContractorLastActivity'

const onboardingFlowColumn = {
  field: OnboardingsTableFields.onboardingFlow,
  headerName: onboardingTableHeaderTitles[OnboardingsTableFields.onboardingFlow],
  flex: 2,
  minWidth: 140,
  sortable: false,
  renderCell: (params: GridRenderCellParams<Onboarding>) =>
    params.value ? <LimitedStringChip text={params.value} /> : null,
}

const invitedAtColumn: GridColDef = {
  field: OnboardingsTableFields.invitedAt,
  headerName: onboardingTableHeaderTitles[OnboardingsTableFields.invitedAt],
  flex: 2,
  minWidth: 140,
  sortable: true,
  type: 'date',
  valueGetter: gridValueGetters.parseDate,
  valueFormatter: gridFormatters.formatShortDate,
}

const needsReviewColumn: GridColDef = {
  field: OnboardingsTableFields.toReviewCount,
  headerName: onboardingTableHeaderTitles[OnboardingsTableFields.toReviewCount],
  flex: 2,
  minWidth: 120,
  sortable: true,
  renderCell: (params: GridRenderCellParams<Onboarding>) =>
    params.value > 0 ? (
      <Tooltip
        title={params.row.documentsToReview.map(getOnboardingTableStepTitle).join(', ')}
        placement="bottom-start"
      >
        <span>
          <Chip
            text={`${params.value} ${addS(Number(params.value), 'step')}`}
            textColor={colors.COLORS_ORANGE_500}
            className={tw`bg-[rgba(255, 238, 214, 1)]`}
          />
        </span>
      </Tooltip>
    ) : null,
}

const sharedOnboardingsColumns: Array<GridColDef> = [
  {
    field: OnboardingsTableFields.contractorId,
    headerName: onboardingTableHeaderTitles[OnboardingsTableFields.contractorId],
    flex: 3,
    minWidth: 120,
    sortable: true,
  },
  {
    field: OnboardingsTableFields.contractorName,
    headerName: onboardingTableHeaderTitles[OnboardingsTableFields.contractorName],
    flex: 2,
    minWidth: 140,
    sortable: true,
  },
  {
    field: OnboardingsTableFields.contractorType,
    headerName: onboardingTableHeaderTitles[OnboardingsTableFields.contractorType],
    flex: 2,
    minWidth: 84,
    sortable: true,
    valueGetter: (params: GridValueGetterParams<Onboarding>) =>
      contractorTypeTitles[params.row.contractorType],
    renderCell: (params: GridRenderCellParams<Onboarding>) => (
      <ContractorTypeChip type={params.row.contractorType} />
    ),
  },
]

const lastOnboardingColumns: Array<GridColDef> = [
  {
    field: OnboardingsTableFields.phone,
    headerName: onboardingTableHeaderTitles[OnboardingsTableFields.phone],
    flex: 2,
    minWidth: 140,
    sortable: true,
  },
  {
    field: OnboardingsTableFields.tags,
    headerName: onboardingTableHeaderTitles[OnboardingsTableFields.tags],
    flex: 3,
    minWidth: 120,
    sortable: false,
    valueGetter: (params: GridValueGetterParams<Onboarding>) => params.row?.tags.join(', '),
    renderCell: (params: GridRenderCellParams<Onboarding>) =>
      params.row?.tags.length ? <TableTags tags={params.row?.tags || []} /> : <span>-</span>,
  },
  {
    field: 'lastActivity',
    headerName: 'Last activity',
    flex: 2,
    minWidth: 120,
    sortable: false,
    valueGetter: (params: GridValueGetterParams<Onboarding>) => {
      return params.row.lastActivity?.createdAt
        ? dayjs(params.row.lastActivity.createdAt).toDate()
        : null
    },
    renderCell: (params: GridRenderCellParams<Onboarding>) => (
      <ContractorLastActivity lastActivity={params.row.lastActivity} />
    ),
  },
]

const invitedContractorsTable: Array<GridColDef> = [
  ...sharedOnboardingsColumns,
  ...lastOnboardingColumns,
  invitedAtColumn,
  onboardingFlowColumn,
  {
    field: OnboardingsTableFields.stepsCompleted,
    headerName: onboardingTableHeaderTitles[OnboardingsTableFields.stepsCompleted],
    flex: 2,
    minWidth: 140,
    sortable: true,
    valueGetter: (params: GridValueGetterParams<Onboarding>) =>
      `${params.row.stepsCompleted} of ${params.row.totalSteps}`,
    renderCell: (params: GridRenderCellParams<Onboarding>) => <Chip text={params.value} />,
  },
  needsReviewColumn,
]

export const onboardingColumns: Record<OnboardingStatus, Array<GridColDef>> = {
  [OnboardingStatus.Created]: invitedContractorsTable,
  [OnboardingStatus.PartiallyCompleted]: invitedContractorsTable,
  [OnboardingStatus.WaitingForApproval]: [
    ...sharedOnboardingsColumns,
    ...lastOnboardingColumns,
    invitedAtColumn,
    onboardingFlowColumn,
    needsReviewColumn,
  ],
  [OnboardingStatus.Completed]: [
    ...sharedOnboardingsColumns,
    ...lastOnboardingColumns,
    invitedAtColumn,
    {
      field: OnboardingsTableFields.isActive,
      headerName: onboardingTableHeaderTitles[OnboardingsTableFields.isActive],
      flex: 1.5,
      sortable: true,
      valueGetter: (params: GridValueGetterParams<Onboarding>) => {
        return params?.value ? 'Active' : 'Inactive'
      },
      renderCell: (params: GridRenderCellParams<Onboarding>) => (
        <Chip
          className={params?.value === 'Active' ? tw`bg-[#04C523]` : tw`bg-[#E33914]`}
          text={params?.value}
          textColor={colors.GRAY_WHITE}
        />
      ),
    },
    {
      field: OnboardingsTableFields.email,
      headerName: onboardingTableHeaderTitles[OnboardingsTableFields.email],
      flex: 3,
      sortable: true,
    },
    {
      field: OnboardingsTableFields.dateOnboarded,
      headerName: onboardingTableHeaderTitles[OnboardingsTableFields.dateOnboarded],
      flex: 2,
      sortable: true,
      type: 'date',
      valueGetter: gridValueGetters.parseDate,
      valueFormatter: gridFormatters.formatShortDate,
    },
    {
      field: OnboardingsTableFields.actions,
      headerName: onboardingTableHeaderTitles[OnboardingsTableFields.actions],
      flex: 1,
      sortable: false,
      disableExport: true,
      renderCell: (params: GridRenderCellParams<Onboarding>) => (
        <OnboardingsItemActionButton onboarding={params.row} />
      ),
    },
  ],
  [OnboardingStatus.Failed]: [
    ...sharedOnboardingsColumns,
    {
      field: OnboardingsTableFields.status,
      headerName: onboardingTableHeaderTitles[OnboardingsTableFields.status],
      flex: 2,
      sortable: false,
      renderCell: (params: GridRenderCellParams<Onboarding>) => {
        return (
          <OnboardingStatusChip
            onboardingStatus={params.row.status}
            // failedAt={params.row.onboardingFailedAt}
            failedSteps={params.row.failedSteps}
          />
        )
      },
    },
    {
      field: OnboardingsTableFields.failedAt,
      headerName: onboardingTableHeaderTitles[OnboardingsTableFields.failedAt],
      flex: 2,
      sortable: true,
      type: 'date',
      valueGetter: gridValueGetters.parseDate,
      valueFormatter: gridFormatters.formatShortDate,
    },
    ...lastOnboardingColumns,
    onboardingFlowColumn,
    {
      field: OnboardingsTableFields.failureReason,
      headerName: onboardingTableHeaderTitles[OnboardingsTableFields.failureReason],
      flex: 3,
      sortable: false,
      valueGetter: (params: GridValueGetterParams<Onboarding>) => {
        if (params.row.failureReason) {
          return [_.capitalize(params.row.failureReason)]
        }

        return params.row.failedSteps.map(getOnboardingTableStepTitle)
      },
      renderCell: (params: GridRenderCellParams<Onboarding>) => <StepsChip titles={params.value} />,
    },
  ],
}

export const onboardingInitialSortFields = {
  [OnboardingStatus.Created]: 'invitedAt',
  [OnboardingStatus.WaitingForApproval]: 'invitedAt',
  [OnboardingStatus.Completed]: 'dateOnboarded',
  [OnboardingStatus.Failed]: undefined,
  [OnboardingStatus.PartiallyCompleted]: undefined,
}

export const onboardingPageSizeOptions = [100, 250, 500, 1000]
